import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '@/api';
import { 
  MapPin, Phone, Mail, Clock, Search, Plus, Trash2,
  ChevronRight, ChevronDown, X, Building2, Navigation, Loader2, Edit2, Save, 
  Upload, ExternalLink, MoreVertical, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GMAPS_KEY;

// Vendor colors - each vendor gets a unique color
const VENDOR_COLORS = [
  '#FF6B35', // Vivid orange — pops on any gray map
  '#FFD600', // Vivid yellow
  '#69FF47', // Bright lime
  '#FF4081', // Hot pink
  '#40C4FF', // Sky blue  (distinct from map water)
  '#E040FB', // Bright purple
  '#FFAB40', // Amber
  '#1DE9B6', // Bright teal
  '#FF1744', // Bright red
  '#B2FF59', // Lime green
];

export default function VendorMap() {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  const [filterState, setFilterState] = useState('');
  
  // Modals
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [showManageVendors, setShowManageVendors] = useState(false);
  const [selectedVendorForLocation, setSelectedVendorForLocation] = useState(null);
  const [editingVendor, setEditingVendor] = useState(null);
  
  // Notes
  const [editingNotes, setEditingNotes] = useState(false);
  const [tempNotes, setTempNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  
  // Expanded vendors in sidebar
  const [expandedVendors, setExpandedVendors] = useState({});

  // Load Google Maps script
  useEffect(() => {
    if (window.google?.maps) {
      initMap();
      return;
    }

    // Pin to the "quarterly" stable channel so API behavior is predictable across
    // releases. v=weekly can silently flip maps into vector/WebGL mode and drop styles.
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&v=quarterly`;
    script.async = true;
    script.defer = true;
    script.onload = () => initMap();
    script.onerror = () => toast.error('Failed to load Google Maps');
    document.head.appendChild(script);

    return () => {
      markersRef.current.forEach(m => m.setMap(null));
    };
  }, []);

  const initMap = useCallback(() => {
    if (!mapRef.current || !window.google?.maps) return;

    const mapStyles = [
      // Land — medium steel gray
      { elementType: 'geometry',               stylers: [{ color: '#2C3A47' }] },
      { elementType: 'labels.text.stroke',     stylers: [{ color: '#1E2A35' }] },
      { elementType: 'labels.text.fill',       stylers: [{ color: '#90A4AE' }] },
      // Roads — highway corridors clearly visible
      { featureType: 'road',                   elementType: 'geometry',         stylers: [{ color: '#3D5166' }] },
      { featureType: 'road.highway',           elementType: 'geometry',         stylers: [{ color: '#4E6478' }] },
      { featureType: 'road.highway',           elementType: 'geometry.stroke',  stylers: [{ color: '#2C3A47' }] },
      { featureType: 'road',                   elementType: 'labels.text.fill', stylers: [{ color: '#78909C' }] },
      { featureType: 'road.highway',           elementType: 'labels.text.fill', stylers: [{ color: '#B0BEC5' }] },
      // Water — darker than land
      { featureType: 'water',                  elementType: 'geometry',         stylers: [{ color: '#0D1B2A' }] },
      { featureType: 'water',                  elementType: 'labels.text.fill', stylers: [{ color: '#435666' }] },
      // Borders
      { featureType: 'administrative',         elementType: 'geometry.stroke',  stylers: [{ color: '#4A5E72' }] },
      { featureType: 'administrative.country', elementType: 'geometry.stroke',  stylers: [{ color: '#506070' }] },
      // Hide noise
      { featureType: 'poi',      elementType: 'labels', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit',                         stylers: [{ visibility: 'off' }] },
    ];

    // CRITICAL: Force the RASTER renderer. Google Maps v3.55+ defaults newer GCP
    // projects to the WebGL/vector renderer, which silently ignores JSON `styles`.
    // RASTER uses the classic tile engine and honors styles 100% of the time.
    const RasterType =
      window.google.maps.RenderingType?.RASTER ??
      'RASTER';

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: { lat: 39.8283, lng: -98.5795 },
      zoom: 4,
      mapTypeId: 'roadmap',
      renderingType: RasterType,     // <- forces raster mode
      styles: mapStyles,             // <- applied directly in constructor
      disableDefaultUI: false,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      gestureHandling: 'greedy',
      backgroundColor: '#2C3A47',    // prevents flash of white while tiles load
    });

    // Belt-and-suspenders: reapply via setOptions. Works on both raster maps and
    // any edge case where constructor styles were dropped.
    mapInstanceRef.current.setOptions({ styles: mapStyles });

    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [vendorsRes, locationsRes] = await Promise.all([
        api.get('/vendor-map/vendors'),
        api.get('/vendor-map/map/locations')
      ]);
      const vendorList = vendorsRes.data.vendors || [];
      const locationList = locationsRes.data.locations || [];
      setVendors(vendorList);
      setLocations(locationList);
      // Expand first vendor by default
      if (vendorList.length > 0) {
        setExpandedVendors({ [vendorList[0].id]: true });
      }
      // markers drawn once by the useEffect that watches `locations`
    } catch (e) {
      toast.error('Failed to load vendor data');
    } finally {
      setLoading(false);
    }
  };

  // Pre-compute vendor→color map (O(1) lookup instead of findIndex every call)
  const vendorColorMap = useMemo(() => {
    const map = {};
    vendors.forEach((v, i) => { map[v.id] = VENDOR_COLORS[i % VENDOR_COLORS.length]; });
    return map;
  }, [vendors]);

  const getVendorColor = useCallback((vendorId) => {
    return vendorColorMap[vendorId] || VENDOR_COLORS[0];
  }, [vendorColorMap]);

  const addMarkersToMap = (locs, vendorList) => {
    if (!mapInstanceRef.current || !window.google?.maps) return;

    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    locs.forEach(loc => {
      if (!loc.latitude || !loc.longitude) return;

      const color = getVendorColor(loc.vendor_id);
      
      const marker = new window.google.maps.Marker({
        position: { lat: loc.latitude, lng: loc.longitude },
        map: mapInstanceRef.current,
        title: loc.name,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 9,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: '#1E2A35',
          strokeWeight: 2,
        },
      });

      marker.addListener('click', () => {
        setSelectedLocation(loc);
        setTempNotes(loc.notes || '');
        mapInstanceRef.current.panTo({ lat: loc.latitude, lng: loc.longitude });
        mapInstanceRef.current.setZoom(10);
      });

      markersRef.current.push(marker);
    });
  };

  // Debounce search so marker rebuild doesn't fire on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Recompute filtered locations only when stable inputs change
  const filteredLocations = useMemo(() => locations.filter(loc => {
    if (filterVendor && loc.vendor_id !== filterVendor) return false;
    if (filterState && loc.state?.toUpperCase() !== filterState.toUpperCase()) return false;
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      return (
        loc.name?.toLowerCase().includes(q) ||
        loc.city?.toLowerCase().includes(q) ||
        loc.state?.toLowerCase().includes(q) ||
        loc.interstate?.toLowerCase().includes(q) ||
        loc.vendor_name?.toLowerCase().includes(q)
      );
    }
    return true;
  }), [locations, filterVendor, filterState, debouncedSearch]);

  // Rebuild map markers only when filteredLocations actually changes
  useEffect(() => {
    if (mapInstanceRef.current) {
      addMarkersToMap(filteredLocations, vendors);
    }
  }, [filteredLocations, vendors]);

  const uniqueStates = [...new Set(locations.map(l => l.state).filter(Boolean))].sort();

  // Group locations by vendor
  const locationsByVendor = vendors.map(vendor => ({
    ...vendor,
    color: getVendorColor(vendor.id),
    locations: filteredLocations.filter(l => l.vendor_id === vendor.id)
  }));

  // Toggle vendor expansion
  const toggleVendor = (vendorId) => {
    setExpandedVendors(prev => ({ ...prev, [vendorId]: !prev[vendorId] }));
  };

  // Save notes
  const handleSaveNotes = async () => {
    if (!selectedLocation) return;
    setSavingNotes(true);
    try {
      await api.patch(`/vendor-map/vendors/${selectedLocation.vendor_id}/locations/${selectedLocation.id}/notes`, {
        notes: tempNotes
      });
      setLocations(prev => prev.map(l => 
        l.id === selectedLocation.id ? { ...l, notes: tempNotes } : l
      ));
      setSelectedLocation(prev => ({ ...prev, notes: tempNotes }));
      setEditingNotes(false);
      toast.success('Notes saved');
    } catch (e) {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  // Import Excel file
  const handleImportFile = async (vendorId, file) => {
    if (!file) return;
    
    const validTypes = ['.xlsx', '.xls', '.csv'];
    const fileExt = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!validTypes.includes(fileExt)) {
      toast.error('Please upload an Excel file (.xlsx, .xls) or CSV');
      return;
    }

    const loadingToast = toast.loading(`Importing ${file.name}...`);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post(`/vendor-map/vendors/${vendorId}/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.dismiss(loadingToast);
      
      if (res.data.imported > 0) {
        toast.success(`Imported ${res.data.imported} locations!`);
        loadData();
      } else {
        toast.warning('No locations were imported. Check the file format.');
      }
      
      if (res.data.errors?.length > 0) {
        console.warn('Import errors:', res.data.errors);
        toast.warning(`${res.data.errors.length} rows had errors`);
      }
    } catch (e) {
      toast.dismiss(loadingToast);
      toast.error(e.response?.data?.detail || 'Failed to import file');
    }
  };

  // Delete vendor
  const handleDeleteVendor = async (vendorId) => {
    if (!confirm('Delete this vendor and all its locations?')) return;
    try {
      await api.delete(`/vendor-map/vendors/${vendorId}`);
      toast.success('Vendor deleted');
      loadData();
    } catch (e) {
      toast.error('Failed to delete vendor');
    }
  };

  // Delete location
  const handleDeleteLocation = async (vendorId, locationId) => {
    if (!confirm('Delete this location?')) return;
    try {
      await api.delete(`/vendor-map/vendors/${vendorId}/locations/${locationId}`);
      toast.success('Location deleted');
      setSelectedLocation(null);
      loadData();
    } catch (e) {
      toast.error('Failed to delete location');
    }
  };

  const openDirections = (loc) => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${loc.latitude},${loc.longitude}`, '_blank');
  };

  return (
    <div className="h-[calc(100vh-64px)] flex" data-testid="vendor-map-page">
      {/* Sidebar */}
      <div className="w-80 bg-[#0D1117] border-r border-[#2D3F5E] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-[#2D3F5E]">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-lg font-bold text-white font-['IBM_Plex_Sans']">Vendor Map</h1>
            <div className="flex gap-2">
              <button
                onClick={() => setShowManageVendors(true)}
                className="p-2 bg-[#161B22] hover:bg-[#2D3F5E] rounded-lg transition-colors"
                title="Manage Vendors"
                data-testid="manage-vendors-btn"
              >
                <Settings size={16} className="text-[#5C6A7A]" />
              </button>
              <button
                onClick={() => setShowAddVendor(true)}
                className="p-2 bg-[#3B82F6] hover:bg-[#2563EB] rounded-lg transition-colors"
                title="Add Vendor"
                data-testid="add-vendor-btn"
              >
                <Plus size={16} className="text-white" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6A7A]" />
            <input
              type="text"
              placeholder="Search locations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-sm text-white placeholder-[#5C6A7A] focus:outline-none focus:border-[#3B82F6]"
              data-testid="search-input"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-2">
            <select
              value={filterVendor}
              onChange={(e) => setFilterVendor(e.target.value)}
              className="flex-1 px-2 py-1.5 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-xs text-white focus:outline-none focus:border-[#3B82F6]"
              data-testid="filter-vendor"
            >
              <option value="">All Vendors</option>
              {vendors.map(v => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
            <select
              value={filterState}
              onChange={(e) => setFilterState(e.target.value)}
              className="w-20 px-2 py-1.5 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-xs text-white focus:outline-none focus:border-[#3B82F6]"
              data-testid="filter-state"
            >
              <option value="">State</option>
              {uniqueStates.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Vendor Groups */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-[#3B82F6]" size={24} />
            </div>
          ) : vendors.length === 0 ? (
            <div className="text-center py-8 px-4">
              <Building2 size={32} className="mx-auto mb-2 text-[#2D3F5E]" />
              <p className="text-[#5C6A7A] text-sm mb-3">No vendors yet</p>
              <button
                onClick={() => setShowAddVendor(true)}
                className="text-[#3B82F6] hover:underline text-sm"
              >
                Add your first vendor
              </button>
            </div>
          ) : (
            <div>
              {locationsByVendor.map(vendor => (
                <div key={vendor.id} className="border-b border-[#2D3F5E]/30">
                  {/* Vendor Header */}
                  <div
                    className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-[#161B22] transition-colors"
                    onClick={() => toggleVendor(vendor.id)}
                  >
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: vendor.color }}
                    />
                    <span className="flex-1 text-sm font-medium text-white truncate">{vendor.name}</span>
                    <span className="text-xs text-[#5C6A7A] mr-2">{vendor.locations.length}</span>
                    {expandedVendors[vendor.id] ? (
                      <ChevronDown size={14} className="text-[#5C6A7A]" />
                    ) : (
                      <ChevronRight size={14} className="text-[#5C6A7A]" />
                    )}
                  </div>

                  {/* Vendor Locations */}
                  {expandedVendors[vendor.id] && (
                    <div className="bg-[#0A0D12]">
                      {/* Action Buttons */}
                      <div className="flex gap-1 px-2 py-2 border-b border-[#2D3F5E]/30">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedVendorForLocation(vendor);
                            setShowAddLocation(true);
                          }}
                          className="flex-1 px-3 py-1.5 text-xs text-[#3B82F6] hover:bg-[#161B22] flex items-center justify-center gap-1.5 rounded transition-colors"
                        >
                          <Plus size={12} />
                          Add Location
                        </button>
                        <label
                          className="flex-1 px-3 py-1.5 text-xs text-[#10B981] hover:bg-[#161B22] flex items-center justify-center gap-1.5 rounded transition-colors cursor-pointer"
                        >
                          <Upload size={12} />
                          Import Excel
                          <input
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            className="hidden"
                            onChange={(e) => handleImportFile(vendor.id, e.target.files[0])}
                          />
                        </label>
                      </div>
                      
                      {vendor.locations.length === 0 ? (
                        <p className="px-4 py-3 text-xs text-[#5C6A7A] italic">No locations yet</p>
                      ) : (
                        vendor.locations.slice(0, 20).map(loc => (
                          <div
                            key={loc.id}
                            onClick={() => {
                              setSelectedLocation(loc);
                              setTempNotes(loc.notes || '');
                              if (mapInstanceRef.current) {
                                mapInstanceRef.current.panTo({ lat: loc.latitude, lng: loc.longitude });
                                mapInstanceRef.current.setZoom(10);
                              }
                            }}
                            className={`px-4 py-2 cursor-pointer transition-colors hover:bg-[#161B22] ${
                              selectedLocation?.id === loc.id ? 'bg-[#1C2432] border-l-2' : 'border-l-2 border-transparent'
                            }`}
                            style={{ borderLeftColor: selectedLocation?.id === loc.id ? vendor.color : 'transparent' }}
                          >
                            <p className="text-sm text-white truncate">{loc.name}</p>
                            <p className="text-xs text-[#5C6A7A]">
                              {loc.city}, {loc.state}
                              {loc.interstate && ` • ${loc.interstate}`}
                            </p>
                          </div>
                        ))
                      )}
                      {vendor.locations.length > 20 && (
                        <p className="px-4 py-2 text-xs text-[#5C6A7A]">
                          +{vendor.locations.length - 20} more locations
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="p-3 border-t border-[#2D3F5E] bg-[#161B22]">
          <div className="flex justify-between text-xs">
            <span className="text-[#5C6A7A]">{vendors.length} vendors</span>
            <span className="text-[#5C6A7A]">{filteredLocations.length} locations</span>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        <div ref={mapRef} className="w-full h-full" data-testid="google-map" />
        
        {/* Selected Location Panel */}
        {selectedLocation && (
          <div className="absolute top-4 right-4 w-80 bg-[#0D1117] border border-[#2D3F5E] rounded-xl shadow-2xl overflow-hidden" data-testid="location-panel">
            <div className="p-4 border-b border-[#2D3F5E] flex items-start justify-between">
              <div className="flex items-start gap-2">
                <div 
                  className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
                  style={{ backgroundColor: getVendorColor(selectedLocation.vendor_id) }}
                />
                <div>
                  <h3 className="font-bold text-white">{selectedLocation.name}</h3>
                  <p className="text-sm text-[#3B82F6]">{selectedLocation.vendor_name}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => handleDeleteLocation(selectedLocation.vendor_id, selectedLocation.id)}
                  className="p-1 hover:bg-red-500/20 rounded text-red-400"
                  title="Delete location"
                >
                  <Trash2 size={14} />
                </button>
                <button
                  onClick={() => setSelectedLocation(null)}
                  className="p-1 hover:bg-[#2D3F5E] rounded"
                >
                  <X size={16} className="text-[#5C6A7A]" />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
              {/* Address */}
              <div className="flex items-start gap-2">
                <MapPin size={14} className="text-[#5C6A7A] mt-0.5 flex-shrink-0" />
                <div className="text-sm text-[#B8C0CC]">
                  <p>{selectedLocation.address}</p>
                  <p>{selectedLocation.city}, {selectedLocation.state} {selectedLocation.zip_code}</p>
                </div>
              </div>

              {/* Interstate */}
              {selectedLocation.interstate && (
                <div className="flex items-center gap-2">
                  <Navigation size={14} className="text-[#5C6A7A]" />
                  <span className="text-sm text-[#B8C0CC]">{selectedLocation.interstate} • {selectedLocation.exit_info}</span>
                </div>
              )}

              {/* Phone */}
              {selectedLocation.phone && (
                <div className="flex items-center gap-2">
                  <Phone size={14} className="text-[#5C6A7A]" />
                  <a href={`tel:${selectedLocation.phone}`} className="text-sm text-[#3B82F6] hover:underline">
                    {selectedLocation.phone}
                  </a>
                </div>
              )}

              {/* Email */}
              {selectedLocation.email && (
                <div className="flex items-center gap-2">
                  <Mail size={14} className="text-[#5C6A7A]" />
                  <a href={`mailto:${selectedLocation.email}`} className="text-sm text-[#3B82F6] hover:underline">
                    {selectedLocation.email}
                  </a>
                </div>
              )}

              {/* Working Hours */}
              {selectedLocation.working_hours && (
                <div className="flex items-start gap-2">
                  <Clock size={14} className="text-[#5C6A7A] mt-0.5" />
                  <span className="text-sm text-[#B8C0CC]">
                    {typeof selectedLocation.working_hours === 'string' 
                      ? selectedLocation.working_hours 
                      : '24 Hours'}
                  </span>
                </div>
              )}

              {/* Location Type */}
              {selectedLocation.location_type && (
                <div className="flex items-center gap-2">
                  <Building2 size={14} className="text-[#5C6A7A]" />
                  <span className="text-sm text-[#B8C0CC]">{selectedLocation.location_type}</span>
                </div>
              )}

              {/* Notes */}
              <div className="pt-2 border-t border-[#2D3F5E]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-[#5C6A7A] uppercase tracking-wide">Notes</span>
                  {editingNotes ? (
                    <button
                      onClick={handleSaveNotes}
                      disabled={savingNotes}
                      className="text-xs text-[#3B82F6] hover:underline flex items-center gap-1"
                    >
                      {savingNotes ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                      Save
                    </button>
                  ) : (
                    <button
                      onClick={() => setEditingNotes(true)}
                      className="text-xs text-[#5C6A7A] hover:text-white flex items-center gap-1"
                    >
                      <Edit2 size={12} />
                      Edit
                    </button>
                  )}
                </div>
                {editingNotes ? (
                  <textarea
                    value={tempNotes}
                    onChange={(e) => setTempNotes(e.target.value)}
                    placeholder="Add notes about this location..."
                    className="w-full h-20 px-2 py-1.5 bg-[#161B22] border border-[#2D3F5E] rounded text-sm text-white placeholder-[#5C6A7A] resize-none focus:outline-none focus:border-[#3B82F6]"
                    data-testid="notes-textarea"
                  />
                ) : (
                  <p className="text-sm text-[#B8C0CC]">
                    {selectedLocation.notes || <span className="text-[#5C6A7A] italic">No notes yet</span>}
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => openDirections(selectedLocation)}
                  className="flex-1 py-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  data-testid="get-directions-btn"
                >
                  <Navigation size={14} />
                  Directions
                </button>
                {selectedLocation.phone && (
                  <a
                    href={`tel:${selectedLocation.phone}`}
                    className="px-4 py-2 bg-[#161B22] hover:bg-[#2D3F5E] text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center"
                  >
                    <Phone size={14} />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-[#0D1117]/90 border border-[#2D3F5E] rounded-lg p-3">
          <p className="text-xs font-medium text-[#5C6A7A] mb-2">Vendors</p>
          <div className="space-y-1">
            {vendors.slice(0, 6).map((vendor, i) => (
              <div key={vendor.id} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: VENDOR_COLORS[i % VENDOR_COLORS.length] }} />
                <span className="text-xs text-[#B8C0CC] truncate max-w-[120px]">{vendor.name}</span>
              </div>
            ))}
            {vendors.length > 6 && (
              <p className="text-xs text-[#5C6A7A]">+{vendors.length - 6} more</p>
            )}
          </div>
        </div>
      </div>

      {/* Add Vendor Dialog */}
      <Dialog open={showAddVendor} onOpenChange={setShowAddVendor}>
        <DialogContent className="bg-[#0D1117] border-[#2D3F5E] text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Vendor</DialogTitle>
          </DialogHeader>
          <AddVendorForm 
            onSuccess={() => {
              setShowAddVendor(false);
              loadData();
            }}
            onCancel={() => setShowAddVendor(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Add Location Dialog */}
      <Dialog open={showAddLocation} onOpenChange={setShowAddLocation}>
        <DialogContent className="bg-[#0D1117] border-[#2D3F5E] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Add Location to {selectedVendorForLocation?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedVendorForLocation && (
            <AddLocationForm
              vendorId={selectedVendorForLocation.id}
              onSuccess={() => {
                setShowAddLocation(false);
                setSelectedVendorForLocation(null);
                loadData();
              }}
              onCancel={() => {
                setShowAddLocation(false);
                setSelectedVendorForLocation(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Manage Vendors Dialog */}
      <Dialog open={showManageVendors} onOpenChange={setShowManageVendors}>
        <DialogContent className="bg-[#0D1117] border-[#2D3F5E] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Manage Vendors</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {vendors.length === 0 ? (
              <p className="text-[#5C6A7A] text-center py-4">No vendors yet</p>
            ) : (
              vendors.map((vendor, i) => (
                <div key={vendor.id} className="flex items-center gap-3 p-3 bg-[#161B22] rounded-lg">
                  <div 
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: VENDOR_COLORS[i % VENDOR_COLORS.length] }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{vendor.name}</p>
                    <p className="text-xs text-[#5C6A7A]">{vendor.location_count || 0} locations</p>
                  </div>
                  <button
                    onClick={() => handleDeleteVendor(vendor.id)}
                    className="p-2 hover:bg-red-500/20 rounded text-red-400"
                    title="Delete vendor"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
          <div className="flex justify-end pt-4 border-t border-[#2D3F5E]">
            <button
              onClick={() => setShowManageVendors(false)}
              className="px-4 py-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg transition-colors"
            >
              Done
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Add Vendor Form Component
function AddVendorForm({ onSuccess, onCancel }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [website, setWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Vendor name is required');
      return;
    }

    setSaving(true);
    try {
      await api.post('/vendor-map/vendors', {
        name: name.trim(),
        description: description.trim() || null,
        website: website.trim() || null,
        main_phone: phone.trim() || null,
        main_email: email.trim() || null,
        service_types: []
      });
      toast.success('Vendor created! Now add locations.');
      onSuccess();
    } catch (e) {
      toast.error('Failed to create vendor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm text-[#5C6A7A] mb-1">Vendor Name *</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Boss Truck Stop, Pilot, Love's"
          className="w-full px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] focus:outline-none focus:border-[#3B82F6]"
          data-testid="vendor-name-input"
          autoFocus
        />
      </div>
      <div>
        <label className="block text-sm text-[#5C6A7A] mb-1">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description of vendor services..."
          className="w-full px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] resize-none h-16 focus:outline-none focus:border-[#3B82F6]"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm text-[#5C6A7A] mb-1">Main Phone</label>
          <input
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 123-4567"
            className="w-full px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
        <div>
          <label className="block text-sm text-[#5C6A7A] mb-1">Main Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="info@vendor.com"
            className="w-full px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm text-[#5C6A7A] mb-1">Website</label>
        <input
          type="text"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="https://www.vendor.com"
          className="w-full px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] focus:outline-none focus:border-[#3B82F6]"
        />
      </div>
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 bg-[#161B22] hover:bg-[#2D3F5E] text-white rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 py-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="save-vendor-btn"
        >
          {saving && <Loader2 size={16} className="animate-spin" />}
          Create Vendor
        </button>
      </div>
    </form>
  );
}

// Add Location Form Component
function AddLocationForm({ vendorId, onSuccess, onCancel }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [interstate, setInterstate] = useState('');
  const [exitInfo, setExitInfo] = useState('');
  const [workingHours, setWorkingHours] = useState('24 Hours');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !city.trim() || !state.trim()) {
      toast.error('Name, city, and state are required');
      return;
    }
    if (!latitude || !longitude) {
      toast.error('Latitude and longitude are required for map display');
      return;
    }

    setSaving(true);
    try {
      await api.post(`/vendor-map/vendors/${vendorId}/locations`, {
        name: name.trim(),
        address: address.trim(),
        city: city.trim(),
        state: state.trim().toUpperCase(),
        zip_code: zipCode.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
        interstate: interstate.trim() || null,
        exit_info: exitInfo.trim() || null,
        notes: notes.trim() || null,
        services: [],
        status: 'active'
      });
      toast.success('Location added');
      onSuccess();
    } catch (e) {
      toast.error('Failed to add location');
    } finally {
      setSaving(false);
    }
  };

  // Get coordinates from address using Google Geocoding
  const geocodeAddress = async () => {
    if (!address && !city) {
      toast.error('Enter an address or city first');
      return;
    }
    
    const fullAddress = `${address}, ${city}, ${state} ${zipCode}`.trim();
    const geocoder = new window.google.maps.Geocoder();
    
    geocoder.geocode({ address: fullAddress }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        setLatitude(loc.lat().toFixed(6));
        setLongitude(loc.lng().toFixed(6));
        toast.success('Coordinates found!');
      } else {
        toast.error('Could not find coordinates for this address');
      }
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-sm text-[#5C6A7A] mb-1">Location Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Dallas West, Exit 42 Location"
            className="w-full px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] focus:outline-none focus:border-[#3B82F6]"
            autoFocus
          />
        </div>
        <div className="col-span-2">
          <label className="block text-sm text-[#5C6A7A] mb-1">Street Address</label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="123 Main St"
            className="w-full px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
        <div>
          <label className="block text-sm text-[#5C6A7A] mb-1">City *</label>
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Dallas"
            className="w-full px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-sm text-[#5C6A7A] mb-1">State *</label>
            <input
              type="text"
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder="TX"
              maxLength={2}
              className="w-full px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] focus:outline-none focus:border-[#3B82F6] uppercase"
            />
          </div>
          <div>
            <label className="block text-sm text-[#5C6A7A] mb-1">Zip</label>
            <input
              type="text"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              placeholder="75001"
              className="w-full px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] focus:outline-none focus:border-[#3B82F6]"
            />
          </div>
        </div>
        
        {/* Coordinates */}
        <div>
          <label className="block text-sm text-[#5C6A7A] mb-1">Latitude *</label>
          <input
            type="text"
            value={latitude}
            onChange={(e) => setLatitude(e.target.value)}
            placeholder="32.7767"
            className="w-full px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
        <div>
          <label className="block text-sm text-[#5C6A7A] mb-1">Longitude *</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              placeholder="-96.7970"
              className="flex-1 px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] focus:outline-none focus:border-[#3B82F6]"
            />
            <button
              type="button"
              onClick={geocodeAddress}
              className="px-3 py-2 bg-[#2D3F5E] hover:bg-[#3B82F6] text-white rounded-lg transition-colors text-xs"
              title="Get coordinates from address"
            >
              <MapPin size={14} />
            </button>
          </div>
        </div>

        {/* Contact */}
        <div>
          <label className="block text-sm text-[#5C6A7A] mb-1">Phone</label>
          <input
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 123-4567"
            className="w-full px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
        <div>
          <label className="block text-sm text-[#5C6A7A] mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="location@vendor.com"
            className="w-full px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] focus:outline-none focus:border-[#3B82F6]"
          />
        </div>

        {/* Interstate */}
        <div>
          <label className="block text-sm text-[#5C6A7A] mb-1">Interstate</label>
          <input
            type="text"
            value={interstate}
            onChange={(e) => setInterstate(e.target.value)}
            placeholder="I-35"
            className="w-full px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
        <div>
          <label className="block text-sm text-[#5C6A7A] mb-1">Exit</label>
          <input
            type="text"
            value={exitInfo}
            onChange={(e) => setExitInfo(e.target.value)}
            placeholder="Exit 42"
            className="w-full px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] focus:outline-none focus:border-[#3B82F6]"
          />
        </div>

        {/* Working Hours */}
        <div className="col-span-2">
          <label className="block text-sm text-[#5C6A7A] mb-1">Working Hours</label>
          <input
            type="text"
            value={workingHours}
            onChange={(e) => setWorkingHours(e.target.value)}
            placeholder="24 Hours / Mon-Fri 8am-6pm"
            className="w-full px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] focus:outline-none focus:border-[#3B82F6]"
          />
        </div>

        {/* Notes */}
        <div className="col-span-2">
          <label className="block text-sm text-[#5C6A7A] mb-1">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional notes about this location..."
            className="w-full px-3 py-2 bg-[#161B22] border border-[#2D3F5E] rounded-lg text-white placeholder-[#5C6A7A] resize-none h-16 focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
      </div>

      <div className="flex gap-3 pt-2 sticky bottom-0 bg-[#0D1117]">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 bg-[#161B22] hover:bg-[#2D3F5E] text-white rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 py-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {saving && <Loader2 size={16} className="animate-spin" />}
          Add Location
        </button>
      </div>
    </form>
  );
}
